import { useEffect, useReducer, useRef, useState } from "react";
import { rootUrl } from "../App";

function useFetchData(path, options = {}) {
    const url = `${rootUrl}${path}`;
    const cache = useRef({});
    const cancelRequest = useRef(false);

    const initialState = {
        loading: false,
        error: undefined,
        data: undefined,
    };

    const fetchReducer = (state, action) => {
        switch (action.type) {
            case "loading":
                return { ...state, loading: true, error: undefined };
            case "fetched":
                return { loading: false, data: action.payload, error: undefined };
            case "error":
                return { loading: false, data: undefined, error: action.payload };
            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(fetchReducer, initialState);
    const [reloadFlag, setReloadFlag] = useState(0);

    const getLSCache = (key) => {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch {
            return null;
        }
    };

    const setLSCache = (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch { }
    };

    const fetchData = async (force = false) => {
        if (!url) return;

        cancelRequest.current = false;
        dispatch({ type: "loading" });

        const now = Date.now();
        const cacheFor = options?.cacheFor || 0; // 0 = no localStorage cache
        const skipCache = options?.skipCache || false; // 🔥 new: skip all caching

        const memoryCache = cache.current[url];
        const storedCache = getLSCache(url);

        // --------------------------
        // 1️⃣ Check in-memory cache
        // --------------------------
        if (!force && !skipCache && memoryCache && (!cacheFor || now - memoryCache.timestamp < cacheFor)) {
            dispatch({ type: "fetched", payload: memoryCache.data });
            return;
        }

        // --------------------------
        // 2️⃣ Check localStorage cache
        // --------------------------
        if (!force && !skipCache && storedCache && (!cacheFor || now - storedCache.timestamp < cacheFor)) {
            cache.current[url] = storedCache; // restore to memory cache
            dispatch({ type: "fetched", payload: storedCache.data });
            return;
        }

        // --------------------------
        // 3️⃣ Live API fetch
        // --------------------------
        try {
    const response = await fetch(url, options);

    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

    // Check if response is JSON
    const contentType = response.headers.get("content-type") || "";
    let data;
    if (contentType.includes("application/json")) {
        data = await response.json();
    } else {
        // fallback for HTML or empty response
        const text = await response.text();
        throw new Error(`Expected JSON but got: ${text.slice(0, 100)}`);
    }

    const cacheEntry = { data, timestamp: now };

    if (!skipCache) {
        cache.current[url] = cacheEntry;
        if (cacheFor) setLSCache(url, cacheEntry);
    }

    if (cancelRequest.current) return;
    dispatch({ type: "fetched", payload: data });

} catch (error) {
    if (cancelRequest.current) return;
    dispatch({ type: "error", payload: error });
}

    };

    // --------------------------
    // Initial & refetch trigger
    // --------------------------
    useEffect(() => {
        fetchData();
        return () => {
            cancelRequest.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, reloadFlag]);

    // --------------------------
    // Manual refetch
    // --------------------------
    const refetch = (force = true) => {
        if (force && cache.current[url]) delete cache.current[url];
        setReloadFlag((prev) => prev + 1);
    };

    return { ...state, refetch };
}

export default useFetchData;